import { PartyTypeCell } from "Components/Users/UserListTable/PartyTypeCell";
import { UserRolesCell } from "Components/Users/UserListTable/UserRolesCell";
import { VerificationCell } from "Components/Users/UserListTable/VerificationCell";
import type { UserDto } from "Infrastructure/Api/Api";
import { useNavigate } from "Navigation/useNavigate";
import { Resources, useResource } from "Translations/Resources";
import { formatDate } from "Utils/DateUtils";
import { appUrl } from "Utils/UrlUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { getIdentityRoleFilterOptions } from "Utils/TableFilterUtils";
import {
  type MRT_ColumnDef,
  type MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";

const RESOURCES = Resources.Pages.Content.AmlUsers;

type Props = {
  data: Array<UserDto>;
  isRefetching?: boolean;
  isLoading?: boolean;
  rowCount: number;
  tableProps: Partial<MRT_TableOptions<UserDto>>;
};

export const AMLUserListTable = ({
  data,
  isRefetching,
  isLoading,
  rowCount,
  tableProps,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useResource();
  const localization = useTableLocalization();
  const COLUMNS = useMemo<Array<MRT_ColumnDef<UserDto>>>(
    () => [
      {
        id: "CustomVerificationsBadge",
        header: t(RESOURCES.TableHeaders.Verification),
        size: 0,
        Cell: ({ row }) => <VerificationCell user={row.original} />,
        filterVariant: "select",
        filterSelectOptions: [
          {
            label: t(Resources.Common.Verification.Verified),
            value: "Verified",
          },
          {
            label: t(Resources.Common.Verification.NotVerified),
            value: "NotVerified",
          },
        ],
        enableSorting: false,
      },
      {
        accessorKey: "party.clientIdentifier",
        header: t(RESOURCES.TableHeaders.ClientIdentifier),
        size: 65,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "login",
        header: t(Resources.Common.Login),
        size: 200,
      },
      {
        accessorKey: "party.companyNumber",
        header: t(RESOURCES.TableHeaders.Company),
        size: 100,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "party.personalNumber",
        header: t(RESOURCES.TableHeaders.PersonalNumber),
        size: 130,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: "roleCode",
        header: t(RESOURCES.TableHeaders.Role),
        Cell: ({ row }) => (
          <UserRolesCell
            roleCode={row.original.roleCode}
            identityRoles={row.original.identityRoles}
          />
        ),
        filterVariant: "multi-select",
        filterSelectOptions: getIdentityRoleFilterOptions(t),
        size: 190,
        // Multi-value cell — the legacy role column sort would not reflect what is displayed.
        enableSorting: false,
      },
      {
        accessorKey: "party.type",
        header: t(RESOURCES.TableHeaders.PartyType),
        Cell: ({ row }) => <PartyTypeCell type={row.original.party.type} />,
        filterVariant: "multi-select",
        filterSelectOptions: [
          {
            label: t(Resources.Common.PartyType.NaturalPerson),
            value: "NaturalPerson",
          },
          {
            label: t(Resources.Common.PartyType.LegalEntity),
            value: "LegalEntity",
          },
          {
            label: t(Resources.Common.PartyType.SelfEmployed),
            value: "SelfEmployed",
          },
        ],
        size: 120,
        enableSorting: false,
      },
      {
        accessorKey: "party.firstName",
        header: t(RESOURCES.TableHeaders.FirstName),
        size: 130,
      },
      {
        accessorKey: "party.lastName",
        header: t(RESOURCES.TableHeaders.LastName),
        size: 130,
      },
      {
        accessorKey: "party.companyName",
        header: t(RESOURCES.TableHeaders.Company),
        size: 200,
      },
      {
        id: "dateCreated",
        accessorFn: ({ dateCreated }) => dateCreated,
        header: t(RESOURCES.TableHeaders.DateCreated),
        size: 170,
        filterVariant: "date",
        Cell: ({ row }) => formatDate(row.original.dateCreated, true),
      },
      {
        accessorKey: "verificationRiskProfile",
        header: t(RESOURCES.TableHeaders.RiskProfile),
        size: 150,
        Cell: ({ row }) =>
          t(
            Resources.Common.RiskProfileStatus[
              row.original.verificationRiskProfile
            ],
          ),
        filterVariant: "multi-select",
        filterSelectOptions: [
          {
            label: t(Resources.Common.RiskProfileStatus.NotVerified),
            value: "NotVerified",
          },
          {
            label: t(Resources.Common.RiskProfileStatus.LowRisk),
            value: "LowRisk",
          },
          {
            label: t(Resources.Common.RiskProfileStatus.MediumRisk),
            value: "MediumRisk",
          },
          {
            label: t(Resources.Common.RiskProfileStatus.HighRisk),
            value: "HighRisk",
          },
          {
            label: t(Resources.Common.RiskProfileStatus.Unacceptable),
            value: "Unacceptable",
          },
        ],
        enableSorting: false,
      },
    ],
    [t],
  );

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns: COLUMNS,
    rowCount,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    localization,
    state: {
      ...tableProps.state,
      isLoading,
      showProgressBars: isRefetching,
    },
    initialState: {
      ...tableProps.initialState,
      density: "compact",
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (e) => {
        const url = `${appUrl("aml-user-detail")}/${row.original.userID}`;
        if (e.metaKey || e.ctrlKey || e.button === 1) {
          window.open(url, "_blank");
        } else {
          navigate(url);
        }
      },
      sx: { cursor: "pointer" },
    }),
    muiTablePaperProps: {
      sx: { borderRadius: "10px" },
    },
  });

  return <MaterialReactTable table={table} />;
};
